import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  Field,
  Input,
  Label,
  RadioGroup,
} from "@headlessui/react";
import { useTranslate } from "@lobby/ocb-intl";
import * as Sentry from "@sentry/react";
import { clsx } from "clsx";
import { useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { Payment, Player } from "../../../../../entities";
import {
  APIError,
  Button,
  Icon,
  Spinner,
  type TPaymentMethodsList as PaymentMethodsList,
  type PaymentCurrency,
  emitter,
  isEmptyArray,
} from "../../../../../shared";
import { CryptoSwapTerminal } from "../../../../CryptoSwapTerminal";
import { NoDataPlaceholder, ProfileSection } from "../../components";

import { PaymentMethodCard } from "./components";
import { createExpireYearPattern, removeNonNumeric } from "./helpers";
import { DepositTabRest } from "./wallet.deposit.rest";

import type { FormEvent } from "react";

const THEMES_WITH_REST_DEPOSIT = ["richbet", "abet", "flexbet", "fantasybet365", "maxibet"];
const LOBBY_THEME = (import.meta as ImportMeta & { env?: { VITE_THEME?: string } }).env?.VITE_THEME;
const USE_REST_DEPOSIT = THEMES_WITH_REST_DEPOSIT.includes(LOBBY_THEME ?? "");

const expireYearPattern = createExpireYearPattern();

const paymentCardKeys = ["name", "cardNumber", "expireMonth", "expireYear", "cardCvv"] as const;

type PaymentMethod = PaymentMethodsList["data"][number] & {
  paymentFields?: PaymentField[];
};

type PaymentMethodType = PaymentMethod["type"];
type PaymentCardKeys = (typeof paymentCardKeys)[number];

type PaymentField = {
  key: PaymentCardKeys;
  label: string;
  validation: Partial<{
    type: string;
    required: boolean;
    pattern: string;
    minLength: number;
    maxLength: number;
  }>;
  options: unknown[];
};

type QRCodeInstructions = {
  qrcodeImage: string;
  paymentCode: string;
};

export function DepositTab() {
  if (USE_REST_DEPOSIT) {
    return <DepositTabRest />;
  }

  return <DepositTabLegacy />;
}

function DepositTabLegacy() {
  const { $t } = useTranslate();

  const { data, isLoading } = Payment.useMethodList("deposit");

  return (
    <Sentry.ErrorBoundary fallback={(error) => <pre>{String(error)}</pre>}>
      <ProfileSection
        isLoading={isLoading}
        data={data}
        placeholder={
          <NoDataPlaceholder
            topText={$t({
              defaultMessage: "Currently, there are no available methods for depositing funds.",
            })}
            bottomText={$t({
              defaultMessage: "Please contact the support chat to deposit funds.",
            })}
            icon={<Icon name="creditCard" className="text-[6.25rem]" />}
          />
        }
      >
        {(validData) => <DepositTabView data={validData} />}
      </ProfileSection>
    </Sentry.ErrorBoundary>
  );
}

function DepositTabView({ data }: { data: PaymentMethod[] }) {
  const { $t } = useTranslate();
  const [selectedMethod, setSelectedMethod] = useState(data[0]);

  const handleMethodChange = (methodId: number) => {
    const newMethod = data.find(({ id }) => id === Number(methodId)) || data[0];
    setSelectedMethod(newMethod);
  };

  const DepositPlanComponent = useMemo(() => {
    if (!selectedMethod) return <></>;

    switch (selectedMethod.type) {
      case "jupiter":
        return <JupiterDepositPlan selectedMethod={selectedMethod} />;
      default:
        return <DefaultDepositPlan selectedMethod={selectedMethod} />;
    }
  }, [selectedMethod]);

  return (
    <div className="gutter-stable relative min-h-0 py-1 mobile-only:pb-5 lg:h-full lg:max-h-full lg:overflow-y-auto">
      <ol className="list-outside list-decimal space-y-10 ps-5">
        <li>
          {$t({ defaultMessage: "Choose one of the payment methods" })}
          <PaymentMethodRadioGroup
            methods={data}
            selectedMethodId={selectedMethod.id}
            onChange={handleMethodChange}
          />
        </li>
        {DepositPlanComponent}
      </ol>
    </div>
  );
}

function JupiterDepositPlan({ selectedMethod }: { selectedMethod: PaymentMethod }) {
  const { $t } = useTranslate();
  const [isCryptoTerminalOpen, setIsCryptoTerminalOpen] = useState(false);

  return (
    <li>
      {$t({ defaultMessage: "Deposit using crypto terminal" })}
      <Button
        variant="secondary"
        className="mt-2.5 mb-3"
        onClick={() => setIsCryptoTerminalOpen((prev) => !prev)}
      >
        <span>{$t({ defaultMessage: "Open terminal" })}</span>
      </Button>

      {isCryptoTerminalOpen &&
        createPortal(
          <div className="fixed inset-0 z-[902] flex items-center justify-center bg-black/80 mobile-only:bg-black overflow-y-auto">
            <div className="lg:max-w-[28.125rem] bg-black lg:outline outline-1 relative outline-[var(--accent-color)] lg:rounded-[1rem] p-2.5 mobile-only:h-full mobile-only:w-full">
              <Button
                variant="secondary"
                fit
                onClick={() => setIsCryptoTerminalOpen(false)}
                className="ms-auto mb-2.5"
              >
                <Icon className="text-14 lg:text-16" name="close" />
              </Button>
              <CryptoSwapTerminal paymentMethodId={selectedMethod.id} />
            </div>
          </div>,
          document.getElementById("root") || document.body,
        )}
    </li>
  );
}

function DefaultDepositPlan({ selectedMethod }: { selectedMethod: PaymentMethod }) {
  const { $t } = useTranslate();
  const [_payCurrency, setPayCurrency] = useState("");
  const [validationError, setValidationError] = useState("");
  const [QRCodeInstructions, setQRCodeInstructions] = useState<QRCodeInstructions | null>(null);
  const formRef = useRef<HTMLFormElement>(null);

  // * Removed until cryptocurrency payments are re-enabled
  // const isCryptocurrencyMethod = selectedMethod.providerStringId === "hero_payments";
  const isCryptocurrencyMethod = false;

  const isRediPayMethod =
    selectedMethod.providerStringId === "redipay" && selectedMethod.type === "card";
  const isCardFormVisible =
    selectedMethod.type === "card" && !isEmptyArray(selectedMethod?.paymentFields);

  const { mutate: deposit, isPending } = Payment.useDeposit();

  const additionalFieldsToRender = useMemo(() => {
    const paymentFields = selectedMethod?.paymentFields;
    if (!paymentFields || isEmptyArray(paymentFields)) {
      return [];
    }

    return paymentFields.filter(({ key }) => !paymentCardKeys.includes(key));
  }, [selectedMethod]);

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();

    if (!formRef.current) {
      throw new Error("Form reference is not set");
    }

    setValidationError("");

    const data = new FormData(formRef.current);

    let methodData = {};

    if (isCardFormVisible) {
      methodData = preparePaymentFields(data);
    } else if (isCryptocurrencyMethod) {
      methodData = {
        payCurrency: data.get("currency[stringId]"),
      };
    } else if (isRediPayMethod) {
      methodData = {
        bankStringId: data.get("bank[stringId]"),
      };
    }

    deposit(
      {
        methodId: selectedMethod.id,
        amount: Math.round(Number(data.get("deposit-amount")) * 100),
        methodData: methodData as unknown as { swapTransaction?: string },
      },
      {
        onSuccess: ({ error, result }) => {
          setQRCodeInstructions(null);

          if (error) {
            const { message, code } = error;
            emitter.emit("ERROR", { error: new APIError(message, { code }) });
          } else if (result?.paymentUrl) {
            if (result.paymentUrlType === "url") {
              window.location.href = result.paymentUrl;
            } else if (result.paymentUrlType === "qrcode" && result.paymentCode) {
              setQRCodeInstructions({
                qrcodeImage: result.paymentUrl,
                paymentCode: result.paymentCode,
              });
            }
          }
        },
      },
    );
  }

  function checkIfAdditionalFieldsExist(fields: PaymentField[]) {
    return fields.some(({ key }) => !paymentCardKeys.includes(key));
  }

  function preparePaymentFields(data: FormData) {
    const paymentFields = selectedMethod?.paymentFields ?? null;

    const additionalFields: Record<string, unknown> = {};
    if (paymentFields && checkIfAdditionalFieldsExist(paymentFields)) {
      for (const field of paymentFields) {
        if (field.key && !paymentCardKeys.includes(field.key) && data.has(field.key)) {
          additionalFields[field.key] = data.get(field.key);
        }
      }
    }

    return {
      cardNumber: String(data.get("cardNumber")).replace(/\s/g, ""),
      expireMonth: Number(data.get("expireMonth")),
      expireYear: Number(data.get("expireYear")),
      cardCvv: Number(data.get("cardCvv")),
      name: String(data.get("name")).trim(),
      ...additionalFields,
    };
  }

  return (
    <>
      <form className="text-14 space-y-10" onSubmit={handleSubmit} ref={formRef}>
        {isCardFormVisible && (
          <li>
            {$t({ defaultMessage: "Enter your card details" })}

            <div className="space-y-2 max-w-[13.75rem]">
              <input
                type="text"
                name="cardNumber"
                inputMode="numeric"
                placeholder={$t({ defaultMessage: "Card number" })}
                pattern="(\d[ ]?){13,19}"
                autoComplete="cc-number"
                maxLength={23}
                minLength={13}
                onInput={(e) => {
                  const target = e.target as HTMLInputElement;

                  let value = removeNonNumeric(target.value);
                  value = value.replace(/(\d{4})(?=\d)/g, "$1 ");

                  if (value !== target.value) {
                    target.value = value;
                  }
                }}
                required
                className="w-full"
              />
              <div className="grid grid-cols-12 gap-1">
                <input
                  type="text"
                  name="expireMonth"
                  inputMode="numeric"
                  pattern="(0[1-9]|1[0-2])"
                  minLength={2}
                  maxLength={2}
                  placeholder={$t({ defaultMessage: "MM" })}
                  autoComplete="cc-exp-month"
                  onInput={(e) => {
                    const target = e.target as HTMLInputElement;
                    target.value = removeNonNumeric(target.value);
                  }}
                  required
                  className="col-span-3"
                />
                <input
                  type="text"
                  name="expireYear"
                  inputMode="numeric"
                  pattern={expireYearPattern}
                  minLength={2}
                  maxLength={2}
                  placeholder={$t({ defaultMessage: "YY" })}
                  autoComplete="cc-exp-year"
                  onInput={(e) => {
                    const target = e.target as HTMLInputElement;
                    target.value = removeNonNumeric(target.value);
                  }}
                  required
                  className="col-span-3"
                />
                <input
                  type="text"
                  name="cardCvv"
                  inputMode="numeric"
                  pattern="\d{3}"
                  maxLength={3}
                  placeholder={$t({ defaultMessage: "CVC" })}
                  autoComplete="cc-csc"
                  onInput={(e) => {
                    const target = e.target as HTMLInputElement;
                    target.value = removeNonNumeric(target.value);
                  }}
                  required
                  className="col-span-3 col-start-10"
                />
              </div>
              <input
                type="text"
                name="name"
                placeholder={$t({ defaultMessage: "Cardholder name" })}
                pattern="\D{5,100}"
                autoComplete="cc-name"
                onInput={(e) => {
                  const target = e.target as HTMLInputElement;
                  target.value = target.value.trimStart();
                }}
                required
                className="w-full [&:not(:placeholder-shown)]:uppercase"
              />

              {additionalFieldsToRender.map((field) => (
                <input
                  key={field.key}
                  name={field.key}
                  placeholder={field.label}
                  className="w-full"
                  {...field.validation}
                />
              ))}
            </div>
          </li>
        )}

        {isRediPayMethod && <RediPayBankList methodId={selectedMethod.id} />}
        {isCryptocurrencyMethod && (
          <CryptocurrencyList methodId={selectedMethod.id} onCurrencyChange={setPayCurrency} />
        )}

        <DepositField
          key={selectedMethod.id}
          selectedMethod={selectedMethod}
          validationError={validationError}
        />
      </form>

      {QRCodeInstructions && (
        <PixPaymentInstructions
          qrcode={QRCodeInstructions.qrcodeImage}
          paymentCode={QRCodeInstructions.paymentCode}
        />
      )}

      {isPending && (
        <div className="fixed mobile-only:absolute inset-0 flex-center backdrop-blur-sm [margin:0!important]">
          <Spinner />
        </div>
      )}
    </>
  );
}

interface DepositFieldProps {
  selectedMethod: PaymentMethod;
  validationError: string;
}

function getDefaultDepositAmount(method: PaymentMethod) {
  const defaultAmount = Number(method.limits?.default);
  return defaultAmount > 0 ? String(defaultAmount / 100) : "";
}

function DepositField({ selectedMethod, validationError }: DepositFieldProps) {
  const { $t } = useTranslate();
  const [depositAmount, setDepositAmount] = useState(() => getDefaultDepositAmount(selectedMethod));
  const inputRef = useRef<HTMLInputElement>(null);

  let depositAmountMin = 0;
  let depositAmountMax;
  if (selectedMethod.limits) {
    const minLimit = selectedMethod.limits.min || 0;
    const maxLimit = selectedMethod.limits.max || 0;

    depositAmountMin = minLimit / 100;
    depositAmountMax = maxLimit / 100;
  }

  return (
    <li>
      {$t({ defaultMessage: "Enter the amount you want to deposit" })}
      <Field disabled={!selectedMethod}>
        <Label className="data-[disabled]:opacity-50 flex items-center gap-1">
          {$t({ defaultMessage: "Amount" })}:
          {/* {isPendingMinAmount ? (
            <Skeleton className="w-32 inline-block h-[0.875rem] bg-[var(--accent-color)]" />
          ) : (
            <span className="ms-1 text-12 text-[var(--accent-color)]">
              {$t(
                { defaultMessage: "(min deposit: {minAmount})" },
                { minAmount: formatMoney(depositAmountMin * 100) },
              )}
            </span>
          )} */}
        </Label>
        <div className="flex mobile-only:flex-col gap-3 lg:gap-2.5 lg:items-center">
          <div className="relative w-52">
            <Input
              ref={inputRef}
              className={clsx("w-full appearance-none data-[disabled]:opacity-50")}
              placeholder={$t({ defaultMessage: "Enter the amount" })}
              name="deposit-amount"
              type="number"
              inputMode="numeric"
              value={depositAmount}
              onChange={(e) => setDepositAmount(e.target.value)}
              min={depositAmountMin}
              max={depositAmountMax}
              step={0.01}
              required
            />
            {/* {isPendingMinAmount && <Skeleton className="absolute inset-0" />} */}
          </div>

          <Button
            variant="success"
            type="submit"
            disabled={!depositAmount}
            className="mobile-only:mx-auto mobile-only:w-fit"
          >
            {$t({ defaultMessage: "Deposit" })}
          </Button>
        </div>
      </Field>

      {validationError && (
        <p className="text-center text-alizarin-crimson leading-none">{validationError}</p>
      )}

      <div
        style={{
          color: "var(--text-secondary-color)",
        }}
        className="mt-2 text-12"
      >
        {$t({
          defaultMessage: "After clicking 'Deposit', you will be redirected to the payment page.",
        })}
      </div>
    </li>
  );
}

function RediPayBankList({ methodId }: { methodId: number }) {
  const { $t } = useTranslate();

  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);

  const playerData = Player.usePlayer();
  const currency = playerData.data?.currency as string;
  const { data: bankListResponse, isPending } = Payment.useBankList(methodId, currency);
  const bankList = bankListResponse?.result.data ?? [];

  const filteredBanks =
    query === ""
      ? bankList
      : bankList.filter((bank) => bank.name.toLowerCase().includes(query.toLowerCase()));

  return (
    <li>
      {$t({ defaultMessage: "Select a bank" })}
      <div>
        <label>{$t({ defaultMessage: "Bank:" })}</label>

        <div className="combobox-wrapper">
          <Combobox
            immediate
            name="bank"
            value={selected}
            onChange={(value) => setSelected(value)}
            onClose={() => setQuery("")}
          >
            <div className="relative">
              <ComboboxInput<(typeof bankList)[0]>
                className="combobox--input"
                placeholder={$t({ defaultMessage: "Select a bank" })}
                displayValue={(bank) => bank?.name}
                onChange={(e) => setQuery(e.target.value)}
                required
              />
              <ComboboxButton className="combobox--button group">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  className="size-4"
                >
                  <path d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z"></path>
                </svg>
              </ComboboxButton>

              {isPending && (
                <div className="absolute left-1/2 top-1/2 -translate-y-1/2 -translate-x-1/2">
                  <Icon name="loading" className="animate-spin" />
                </div>
              )}
            </div>

            <ComboboxOptions anchor="bottom" className="combobox--options">
              {filteredBanks.map((bank) => (
                <ComboboxOption key={bank.stringId} value={bank} className="combobox--option">
                  <div className="text-sm/6">{bank.name}</div>
                </ComboboxOption>
              ))}
            </ComboboxOptions>
          </Combobox>
        </div>
      </div>
    </li>
  );
}

interface CryptocurrencyListProps {
  methodId: number;
  onCurrencyChange: (currency: string) => void;
}

function CryptocurrencyList({ methodId, onCurrencyChange }: CryptocurrencyListProps) {
  const { $t } = useTranslate();

  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<PaymentCurrency | null>(null);

  const playerData = Player.usePlayer();
  const currency = playerData.data?.currency as string;
  const { data: currencyListResponse, isPending } = Payment.useCurrencyList(methodId, currency);
  const currencyList = currencyListResponse?.result?.data ?? [];

  const filteredCurrencies =
    query === ""
      ? currencyList
      : currencyList.filter((currency) =>
          currency.name.toLowerCase().includes(query.toLowerCase()),
        );

  return (
    <li>
      {$t({ defaultMessage: "Select a currency" })}
      <div>
        <label>{$t({ defaultMessage: "Currency:" })}</label>

        <div className="combobox-wrapper">
          <Combobox<PaymentCurrency>
            immediate
            name="currency"
            value={selected || undefined}
            onChange={(value) => {
              setSelected(value);
              onCurrencyChange(value?.stringId ?? "");
            }}
            onClose={() => setQuery("")}
          >
            <div className="relative">
              <ComboboxInput<PaymentCurrency>
                className="combobox--input"
                placeholder={$t({ defaultMessage: "Select a currency" })}
                displayValue={(currency) => currency?.name}
                onChange={(e) => setQuery(e.target.value)}
                required
              />
              <ComboboxButton className="combobox--button group">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  className="size-4"
                >
                  <path d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z"></path>
                </svg>
              </ComboboxButton>

              {isPending && (
                <div className="absolute left-1/2 top-1/2 -translate-y-1/2 -translate-x-1/2">
                  <Icon name="loading" className="animate-spin" />
                </div>
              )}
            </div>

            <ComboboxOptions anchor="bottom" className="combobox--options">
              {filteredCurrencies.map((currency) => (
                <ComboboxOption
                  key={currency.stringId}
                  value={currency}
                  className="combobox--option"
                >
                  <div className="text-sm/6">{currency.name}</div>
                </ComboboxOption>
              ))}
            </ComboboxOptions>
          </Combobox>
        </div>
      </div>
    </li>
  );
}

interface PaymentMethodRadioGroupProps {
  methods: PaymentMethod[];
  selectedMethodId: number;
  onChange: (methodId: number) => void;
}

function PaymentMethodRadioGroup({
  methods,
  selectedMethodId,
  onChange,
}: PaymentMethodRadioGroupProps) {
  return (
    <RadioGroup
      value={selectedMethodId}
      onChange={onChange}
      name="payment-method"
      className="mt-2.5"
    >
      <ul className="flex flex-wrap gap-2.5">
        {methods.map(({ id, name, logoUrl, commission, limits }, idx) => (
          <li key={idx}>
            <PaymentMethodCard
              id={id}
              name={name}
              logoUrl={logoUrl}
              commission={commission}
              limits={limits}
            />
          </li>
        ))}
      </ul>
    </RadioGroup>
  );
}

interface PixPaymentInstructionsProps {
  className?: string;
  qrcode: string;
  paymentCode: string;
}

function PixPaymentInstructions({ className, qrcode, paymentCode }: PixPaymentInstructionsProps) {
  const { $t } = useTranslate();
  const [copied, setCopied] = useState(false);
  const timeoutID = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleCopy = () => {
    navigator.clipboard.writeText(paymentCode);
    setCopied(true);

    if (timeoutID.current) {
      clearTimeout(timeoutID.current);
    }
    timeoutID.current = setTimeout(setCopied, 2000, false);
  };

  return (
    <div className={className}>
      <div className="mb-3 text-center lg:text-lg">
        {$t({ defaultMessage: "Choose a payment method" })}
      </div>
      <div className="flex gap-3 text-xs lg:text-sm">
        <div className="max-w-40">
          <div>{$t({ defaultMessage: "Scan the QR code:" })}</div>
          <div className="mt-1">
            <img
              src={`data:image/jpeg;base64,${qrcode}`}
              alt="QR Code"
              className="size-40 min-w-40"
            />
          </div>
        </div>
        <div className="min-w-0">
          <div>{$t({ defaultMessage: "Copy and paste ID:" })}</div>
          <Input
            type="text"
            value={paymentCode}
            readOnly
            className="outline-[var(--accent-color)] max-w-full mt-1"
          />
          <div className="relative w-fit">
            <Button variant="secondary" onClick={handleCopy} className="my-3">
              <span>{$t({ defaultMessage: "Copy ID" })}</span>
              <span
                className={clsx(
                  "border-[var(--accent-color)] border-1 -top-1 absolute left-1/2 bg-black -translate-x-1/2 -translate-y-1 bg-gray-800 text-xs rounded-rounded px-2 py-1",
                  copied ? "opacity-100" : "opacity-0 transition-opacity duration-300",
                )}
              >
                {$t({ defaultMessage: "Copied!" })}
              </span>
            </Button>
          </div>
          <div className="font-semibold">
            {$t({ defaultMessage: "Payment instructions with ID:" })}
          </div>
          <ol className="ps-5 mb-4 list-decimal">
            <li>{$t({ defaultMessage: "Copy the transaction ID" })}</li>
            <li>
              {$t({
                defaultMessage: "Use the ID to identify your payment in your bank's app",
              })}
            </li>
          </ol>
        </div>
      </div>
    </div>
  );
}
